import { z } from 'zod';
import BN from 'bignumber.js';
import type {
  BaseObjectDetailAddition,
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sessionDetailSchema = z.object({
  pid: z.number({ required_error: 'PID', invalid_type_error: 'PID' }),
  datname: z.string({ required_error: 'Database Name' }).min(1, 'Database Name'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
  init: nullishAsUndefined(z.boolean().optional()),
});

export const sqlDetailSchema = z
  .object({
    datname: z.string({ required_error: 'Database Name' }).min(1, 'Database Name'),
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    queryId: nullishAsUndefined(z.union([z.string(), z.number(), z.instanceof(BN)]).optional()),
    sqlId: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    user: nullishAsUndefined(z.string().optional()),
    userName: nullishAsUndefined(z.string().optional()),
    dbUserName: nullishAsUndefined(z.string().optional()),
    usename: nullishAsUndefined(z.string().optional()),
    username: nullishAsUndefined(z.string().optional()),
    sqlTextHashId: nullishAsUndefined(z.string().optional()),
    dbId: nullishAsUndefined(z.coerce.number().optional()),
    dbUserId: z.preprocess(
      (val) => (val === '' ? undefined : val),
      nullishAsUndefined(z.coerce.number().optional()),
    ),
  })
  .transform(({ queryId, user, userName, dbUserName, usename, username, ...rest }) => ({
    ...rest,
    queryId: queryId != null ? String(queryId) : undefined,
    userName: user ?? userName ?? dbUserName ?? usename ?? username,
  }))
  .pipe(
    z
      .object({
        queryId: nullishAsUndefined(z.string().optional()),
        sqlId: nullishAsUndefined(z.string().optional()),
        datname: z.string().min(1, 'Database Name'),
        instanceId: z.string().min(1, 'Instance ID'),
        fromTime: nullishAsUndefined(z.string().optional()),
        toTime: nullishAsUndefined(z.string().optional()),
        userName: nullishAsUndefined(z.string().optional()),
        sqlTextHashId: nullishAsUndefined(z.string().optional()),
        dbId: nullishAsUndefined(z.number().optional()),
        dbUserId: nullishAsUndefined(z.number().optional()),
      })
      .refine(({ queryId, sqlId }) => queryId != null || sqlId != null, {
        message: 'Query ID, SQL ID',
        path: ['queryId'],
      }),
  );

export const objectDetailSchema = z
  .object({
    objectName: z.string({ required_error: 'Object Name' }).min(1, 'Object Name'),
    databaseName: nullishAsUndefined(z.string().optional()),
    datname: nullishAsUndefined(z.string().optional()),
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    schemaName: nullishAsUndefined(z.string().optional()),
    relId: z.coerce.number({ required_error: 'Rel ID', invalid_type_error: 'Rel ID' }),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    paTrendFromTime: nullishAsUndefined(z.string().optional()),
    paTrendToTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ databaseName, datname, ...rest }) => ({
    ...rest,
    databaseName: databaseName ?? datname,
  }))
  .pipe(
    z.object({
      objectName: z.string().min(1, 'Object Name'),
      databaseName: z.string({ required_error: 'Database Name' }).min(1, 'Database Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      schemaName: nullishAsUndefined(z.string().optional()),
      relId: z.coerce.number({ required_error: 'Rel ID', invalid_type_error: 'Rel ID' }),
      fromTime: nullishAsUndefined(z.string().optional()),
      toTime: nullishAsUndefined(z.string().optional()),
      paTrendFromTime: nullishAsUndefined(z.string().optional()),
      paTrendToTime: nullishAsUndefined(z.string().optional()),
    }),
  );

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z.object({
  sqlTextHashId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'SQL Text Hash ID' }).min(1, 'SQL Text Hash ID'),
  ),
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ObjectDetailSchema = z.infer<typeof objectDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type PostgresqlSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type PostgresqlSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type PostgresqlObjectDetailAddition = BaseObjectDetailAddition & ObjectDetailSchema;
export type PostgresqlParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
